import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Image } from '@codes/ui-kit/components/ui/image';
import { ChatView } from '@lemoncloud/ssocio-chatbots-api';
import { Loader2, MessageSquare } from 'lucide-react';

import { AiLogoDarkImage, AiLogoImage } from '@codes/assets';

import { ChatMessage } from './ChatMessage';
import { ChatMessageSkeleton } from './ChatMessageSkeleton';
import { DocumentModal } from './DocumentModal';

interface ChatViewWithError extends ChatView {
    isError?: boolean;
}

interface DocumentData {
    id: string;
    title: string;
    content: string;
    name?: string;
}

export interface ChatMessagesProps {
    messages: ChatViewWithError[];
    isWaitingResponse: boolean;
    isLoading?: boolean;
    hasInitialized?: boolean;
    onDocumentSelect?: (documentId: string) => Promise<DocumentData | null>;
    onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
    isLoadingMore?: boolean;
    hasMore?: boolean;
    emptyText?: React.ReactNode;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({
    messages,
    isWaitingResponse,
    isLoading = false,
    hasInitialized = true,
    onDocumentSelect,
    onScroll,
    isLoadingMore = false,
    hasMore = false,
    emptyText,
}) => {
    const { t } = useTranslation();
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const isNearBottomRef = useRef(true);

    // Document modal state
    const [documentModalOpen, setDocumentModalOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
    const [loadingDocument, setLoadingDocument] = useState(false);

    // Handle document selection
    const handleDocumentSelect = async (documentId: string) => {
        if (!onDocumentSelect) {
            return;
        }

        setLoadingDocument(true);
        setDocumentModalOpen(true);
        setSelectedDocument(null); // Reset previous document

        try {
            const documentData = await onDocumentSelect(documentId);
            setSelectedDocument(documentData);
        } catch (error) {
            console.error('Failed to load document:', error);
            setSelectedDocument(null);
        } finally {
            setLoadingDocument(false);
        }
    };

    // Handle modal close
    const handleModalClose = (open: boolean) => {
        setDocumentModalOpen(open);
        if (!open) {
            setSelectedDocument(null);
            setLoadingDocument(false);
        }
    };

    // Check if user is near bottom
    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            const threshold = 100;
            isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < threshold;

            if (onScroll) {
                onScroll(e);
            }
        },
        [onScroll]
    );

    // Auto scroll to bottom when new messages arrive (only if user is near bottom and not loading more)
    useEffect(() => {
        if (chatContainerRef.current && !isLoadingMore && isNearBottomRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isLoadingMore]);

    if (isLoading) {
        return (
            <div ref={chatContainerRef} className="flex-1 overflow-auto px-4 py-6">
                <div className="mx-auto space-y-4">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <ChatMessageSkeleton key={index} isQuery={index % 2 === 0} />
                    ))}
                </div>
            </div>
        );
    }

    if (hasInitialized && !isLoading && !messages.length) {
        return (
            <div ref={chatContainerRef} className="flex-1 flex items-center justify-center px-4">
                <div className="text-center max-w-md mx-auto">
                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <MessageSquare className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div className="text-muted-foreground text-base whitespace-pre-line leading-relaxed">
                        {emptyText}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                ref={chatContainerRef}
                className="flex-1 overflow-auto px-4 py-6 scroll-smooth"
                onScroll={handleScroll}
            >
                <div className="mx-auto space-y-4">
                    {isLoadingMore && hasMore && (
                        <div className="flex items-center justify-center py-4">
                            <div className="flex items-center gap-3 text-muted-foreground">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm font-medium">
                                    {t('agent.catalog.chat.loading.more', '이전 메시지를 불러오는 중...')}
                                </span>
                            </div>
                        </div>
                    )}

                    {messages.map((message, index) => (
                        <ChatMessage
                            key={`${message.id}-${message.childNo || message.no || index}-${
                                message.createdAt || Date.now()
                            }`}
                            message={message}
                            onDocumentSelect={handleDocumentSelect}
                        />
                    ))}

                    {isWaitingResponse && (
                        <div className="flex items-center gap-2 text-title relative group max-w-[85%] p-[10px] rounded-[18px]">
                            <Image src={AiLogoImage} darkSrc={AiLogoDarkImage} className="w-5 h-5" alt="AI Assistant" />
                            <div className="flex items-center gap-1 p-3 rounded-lg">
                                <div className="animate-bounce">•</div>
                                <div className="animate-bounce [animation-delay:0.2s]">•</div>
                                <div className="animate-bounce [animation-delay:0.4s]">•</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Document Modal - Outside scrollable container */}
            <DocumentModal
                isOpen={documentModalOpen}
                onOpenChange={handleModalClose}
                documentData={selectedDocument}
                isLoading={loadingDocument}
            />
        </>
    );
};
